// Histogram body — value distribution for numeric features. Answers
// "what's typical?" rather than "how is it trending?" (which is what
// LineChartBody shows).
//
// This is the standalone feature-chart wrapper: it owns the fetch and the
// ChartTimeRangePicker, then hands the windowed points to the shared
// HistogramChart core (which holds the binning + bar rendering, so the
// summary card's Distribution view renders identically). All series share
// /api/features/timeseries with LineChartBody so counts match the line.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import HistogramChart, { type TimeSeriesDataPoint } from './HistogramChart';

const HistogramBody: React.FC<ChartBodyProps> = ({ featureId, unit, config, fullscreen }) => {
  const [rawData, setRawData] = useState<TimeSeriesDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get<{ date: string; value: number }[]>(`/api/features/timeseries/${featureId}`)
      .then((response) => {
        if (cancelled) return;
        const processed = response.data.map((item) => ({
          value: item.value,
          date: new Date(item.date).getTime(),
        }));
        setRawData(processed);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch time series data:', err);
        if (!cancelled) setError('Failed to load chart data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const dataDates = useMemo(() => rawData.map((r) => r.date), [rawData]);
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );
  const data = useMemo(
    () => rawData.filter((d) => withinWindow(d.date, win)),
    [rawData, win],
  );

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rawData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No numeric data available for this feature.</p>
        <p className="text-sm">Add check-ins with numeric values to see a distribution.</p>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} />
      <div className={fullscreen ? 'flex-1 min-h-0' : 'h-80'}>
        <HistogramChart data={data} config={config} unit={unit} fullscreen={!!fullscreen} />
      </div>
    </div>
  );
};

export default HistogramBody;
